import React, { useRef, useState } from "react";

const featureCards = [
  {
    title: "Concierge intake",
    copy: "Share your restaurant, date flexibility, party size, and contact details so Tock Buddy can work from one clean reservation brief."
  },
  {
    title: "24/7 availability watch",
    copy: "Tock Buddy monitors high-demand restaurants around the clock and reacts when a real booking window opens."
  },
  {
    title: "Account handoff",
    copy: "When a reservation is secured, you receive alerts, pay the concierge deposit, and get the Tock account credentials created for your booking."
  }
];

const steps = [
  {
    title: "Request a reservation",
    copy: "Submit the manual booking form with your name, phone, email, restaurant, party size, and preferred date."
  },
  {
    title: "We create the Tock account",
    copy: "A Tock account is created in the customer's name so the final reservation is tied to the diner who will use it."
  },
  {
    title: "Tock Buddy watches 24/7",
    copy: "The service searches for availability continuously across release windows, cancellations, and any-date openings."
  },
  {
    title: "You get the success alert",
    copy: "Once a booking is found, the customer receives email and text alerts to pay the $100/person concierge fee."
  },
  {
    title: "Credentials are released",
    copy: "After the deposit is received, the Tock account login is released to the customer for direct reservation access."
  }
];

const supportedRestaurants = [
  {
    name: "Hayato",
    location: "Los Angeles",
    detail: "Concierge monitoring for one of LA's most competitive Tock releases, including flexible any-date requests."
  },
  {
    name: "Fu Hui Hua",
    location: "San Francisco",
    detail: "Continuous booking watch for limited San Francisco availability, weekly drops, and cancellation openings."
  },
  {
    name: "Atomix",
    location: "New York City",
    detail: "Reservation concierge support for date-targeted and flexible Atomix requests around monthly release behavior."
  },
  {
    name: "The French Laundry",
    location: "Yountville, Napa Valley",
    detail: "Dedicated Tock Dining Room monitoring for The French Laundry, with support for targeted dates, party sizes, release windows, and cancellation openings."
  }
];

const availableReservations = [
  {
    id: "french-laundry-2026-12-03-1600",
    restaurant: "The French Laundry",
    location: "Yountville, Napa Valley",
    date: "2026-12-03",
    dateLabel: "December 3, 2026",
    dateTime: "2026-12-03T16:00:00-08:00",
    guestCount: "2",
    time: "4:00 PM"
  },
  {
    id: "french-laundry-2026-12-20-1600",
    restaurant: "The French Laundry",
    location: "Yountville, Napa Valley",
    date: "2026-12-20",
    dateLabel: "December 20, 2026",
    dateTime: "2026-12-20T16:00:00-08:00",
    guestCount: "2",
    time: "4:00 PM"
  }
];

const reviewImages = [
  {
    src: "/reviews/account-handoff-redacted.png",
    alt: "Redacted customer text conversation confirming an account handoff.",
    caption: "Account handoff confirmation"
  },
  {
    src: "/reviews/customer-feedback-redacted.png",
    alt: "Redacted customer chat feedback describing an easy booking experience.",
    caption: "Customer chat feedback"
  },
  {
    src: "/reviews/email-feedback-redacted.png",
    alt: "Redacted customer email confirming a completed booking handoff.",
    caption: "Email confirmation"
  }
];

const bookingRestaurantOptions = [
  "Hayato",
  "Fu Hui Hua",
  "Atomix",
  "The French Laundry",
  "Other Tock restaurant"
];

const faqs = [
  {
    question: "What is Tock Buddy?",
    answer: "Tock Buddy is a concierge reservation service for hard-to-book Tock restaurants. Customers submit a request, and Tock Buddy monitors for availability until a booking can be secured."
  },
  {
    question: "Why is a Tock account created for me?",
    answer: "Creating the Tock account in the customer's name helps guarantee the reservation is connected to the person using it. Once the concierge deposit is received, the login credentials are released to the customer."
  },
  {
    question: "When do I pay the $100/person concierge fee?",
    answer: "Payment is only requested after a booking is found. You receive an email and text alert, then pay the $100/person concierge fee before the Tock account credentials are handed off."
  },
  {
    question: "Can I request any available date?",
    answer: "Yes. You can choose a specific target date or select any-date mode so Tock Buddy can pursue the first suitable opening."
  }
];

const LandingPage = () => {
  const bookingRequestRef = useRef(null);
  const [submitState, setSubmitState] = useState({
    status: "idle",
    message: ""
  });
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    restaurant: bookingRestaurantOptions[0],
    customRestaurant: "",
    anyDate: true,
    desiredDate: "",
    guestCount: "2",
    notes: ""
  });

  const resolvedRestaurant = formData.restaurant === "Other Tock restaurant"
    ? formData.customRestaurant.trim()
    : formData.restaurant;

  async function handleBookingSubmit(event) {
    event.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setSubmitState({
        status: "error",
        message: "Please fill in your first name, last name, email, and phone number."
      });
      return;
    }

    if (!resolvedRestaurant) {
      setSubmitState({
        status: "error",
        message: "Please choose a restaurant or enter the restaurant name."
      });
      return;
    }

    if (!formData.anyDate && !formData.desiredDate) {
      setSubmitState({
        status: "error",
        message: "Please choose a date or switch to any-date mode."
      });
      return;
    }

    setSubmitState({
      status: "submitting",
      message: "Sending your booking request..."
    });

    try {
      const requestResponse = await fetch("/api/booking-request/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          restaurant: resolvedRestaurant,
          anyDate: formData.anyDate,
          desiredDate: formData.anyDate ? "" : formData.desiredDate,
          guestCount: formData.guestCount,
          notes: formData.notes.trim()
        })
      });

      const requestPayload = await requestResponse.json();
      if (!requestResponse.ok) {
        throw new Error(requestPayload.error || "Unable to submit the booking request.");
      }

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        restaurant: bookingRestaurantOptions[0],
        customRestaurant: "",
        anyDate: true,
        desiredDate: "",
        guestCount: "2",
        notes: ""
      });
      setSubmitState({
        status: "success",
        message: "Request submitted. Tock Buddy will monitor for availability 24/7. If we secure the booking, you will receive an email and text alert to pay the $100/person concierge fee before we release the Tock account credentials created for your reservation."
      });
    } catch (error) {
      setSubmitState({
        status: "error",
        message: error.message || "Something went wrong while submitting the request."
      });
    }
  }

  function handleInputChange(event) {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : name === "phone" ? formatPhoneNumber(value) : value
    }));
  }

  function handleReservationInterest(reservation) {
    setFormData((current) => ({
      ...current,
      restaurant: reservation.restaurant,
      customRestaurant: "",
      anyDate: false,
      desiredDate: reservation.date,
      guestCount: reservation.guestCount,
      notes: `Interested in the currently available ${reservation.restaurant} reservation on ${reservation.dateLabel} at ${reservation.time} for ${reservation.guestCount} people.`
    }));
    setSubmitState({
      status: "idle",
      message: `The ${reservation.dateLabel} at ${reservation.time} opening has been added to your request. Complete your contact details and submit it so we can follow up.`
    });

    window.setTimeout(() => {
      bookingRequestRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }

  function formatPhoneNumber(value) {
    const digits = value.replace(/\D/g, "").slice(0, 10);

    if (digits.length <= 3) {
      return digits;
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return (
    <div className="min-h-screen bg-[#f4efe6] text-stone-900">
      <div className="fixed inset-x-0 top-0 -z-10 h-[38rem] bg-[radial-gradient(circle_at_top_right,_rgba(168,85,42,0.2),_transparent_35%),radial-gradient(circle_at_left,_rgba(24,77,71,0.14),_transparent_30%),linear-gradient(180deg,_#fffaf2_0%,_#f4efe6_65%,_#efe7da_100%)]" />

      <header className="sticky top-0 z-50 border-b border-stone-200/70 bg-[#fffaf2]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 lg:px-8">
          <a href="#top" className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#8f3d24,#184d47)] text-sm font-extrabold text-[#fff7ef] shadow-[0_10px_30px_rgba(53,33,19,0.18)]">
              TB
            </div>
            <div>
              <div className="font-['Fraunces'] text-xl font-semibold tracking-tight">Tock Buddy</div>
              <div className="text-xs uppercase tracking-[0.22em] text-stone-500">Booking Concierge</div>
            </div>
          </a>

          <nav className="hidden items-center gap-6 text-sm font-semibold text-stone-600 md:flex">
            <a href="#features" className="transition hover:text-stone-900">Features</a>
            <a href="#how-it-works" className="transition hover:text-stone-900">How it works</a>
            <a href="#booking-request" className="transition hover:text-stone-900">Booking request</a>
            <a href="#faq" className="transition hover:text-stone-900">FAQ</a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="mx-auto max-w-6xl px-5 pb-16 pt-16 lg:px-8 lg:pb-24 lg:pt-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <div className="mb-5 inline-flex items-center rounded-full border border-stone-300 bg-[#fffaf2] px-4 py-2 text-sm font-semibold text-stone-700 shadow-sm">
                Concierge booking support for Tock reservation drops and high-demand chef&apos;s counters
              </div>
              <h1 className="max-w-4xl font-['Fraunces'] text-5xl leading-[1.02] tracking-tight text-stone-900 md:text-7xl">
                Request the reservation. Tock Buddy watches until a table opens.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-stone-600 md:text-xl">
                Submit a manual booking request for restaurants like Hayato, Atomix, Fu Hui Hua, and The French Laundry. We create a Tock account in the customer&apos;s name, monitor availability 24/7, alert you when a booking is found, and release the login credentials after the $100/person concierge deposit is received.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="#booking-request"
                  className="inline-flex items-center justify-center rounded-2xl border border-stone-300 bg-[#fffaf2] px-6 py-4 text-base font-bold text-stone-800 transition hover:border-stone-400 hover:bg-white"
                >
                  Start a booking request
                </a>
              </div>

              <div className="mt-8 grid gap-3 text-sm text-stone-600 sm:grid-cols-3">
                <div className="rounded-2xl border border-stone-200 bg-white/75 px-4 py-3 shadow-sm">24/7 availability monitoring</div>
                <div className="rounded-2xl border border-stone-200 bg-white/75 px-4 py-3 shadow-sm">$100/person only after success</div>
                <div className="rounded-2xl border border-stone-200 bg-white/75 px-4 py-3 shadow-sm">Account credentials released after deposit</div>
              </div>
            </div>

            <div className="relative soft-float">
              <div className="absolute -inset-4 rounded-[2rem] bg-[radial-gradient(circle,_rgba(24,77,71,0.16),_transparent_60%)] blur-2xl" />
              <div className="hero-panel relative overflow-hidden rounded-[2rem] border border-stone-200 bg-[#fffaf4] p-6 shadow-[0_22px_70px_rgba(43,30,20,0.14)]">
                <div className="mb-5 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.22em] text-stone-500">Concierge dashboard</div>
                    <div className="mt-2 font-['Fraunces'] text-3xl text-stone-900">Manual request</div>
                  </div>
                  <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-800">
                    24/7 watch
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="scan-line rounded-2xl border border-stone-200 bg-white p-4">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Active request</div>
                    <div className="mt-3 text-sm leading-7 text-stone-700">
                      Restaurant: Hayato
                      <br />
                      Date: Any date
                      <br />
                      Guests: 2
                      <br />
                      Status: Watching release windows and cancellations.
                    </div>
                  </div>

                  <div className="rounded-2xl border border-stone-200 bg-white p-4">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Concierge flow</div>
                    <div className="mt-3 space-y-2 text-sm text-stone-700">
                      <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2">
                        <span>Request received</span>
                        <span className="font-semibold text-stone-900">Intake</span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2">
                        <span>Tock account</span>
                        <span className="font-semibold text-stone-900">Created</span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2">
                        <span>Success alert</span>
                        <span className="font-semibold text-stone-900">Email + text</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-[#173b38] p-5 text-[#f7f1e8]">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-100/80">Why people use it</div>
                    <p className="mt-3 text-sm leading-7 text-emerald-50/90">
                      Tock Buddy is designed for diners who want a trusted concierge to keep watch, move fast, and hand over confirmed reservation access cleanly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="available-reservations" aria-labelledby="current-french-laundry-openings" className="border-y border-stone-200/70 bg-[#f7f0e6] py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">Current openings</div>
                <h2 id="current-french-laundry-openings" className="mt-3 font-['Fraunces'] text-4xl leading-tight text-stone-900">
                  Current The French Laundry reservation openings.
                </h2>
                <p className="mt-4 text-lg leading-8 text-stone-600">
                  These confirmed The French Laundry reservations in Yountville can move quickly. Send an interest request for the exact date, time, and party size below, and we will follow up with the next steps.
                </p>
              </div>
              <div className="inline-flex w-fit items-center rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-900">
                Available now
              </div>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {availableReservations.map((reservation) => (
                <article
                  key={reservation.id}
                  aria-labelledby={`${reservation.id}-title`}
                  className="rounded-[1.8rem] border border-stone-200 bg-white p-7 shadow-[0_14px_40px_rgba(63,41,27,0.08)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">{reservation.location}</div>
                      <h3 id={`${reservation.id}-title`} className="mt-3 font-['Fraunces'] text-3xl text-stone-900">{reservation.restaurant}</h3>
                    </div>
                    <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-900">Open</span>
                  </div>

                  <div className="mt-7 grid grid-cols-2 gap-3 rounded-2xl bg-[#fcf7f1] p-4 text-stone-800">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500">Date</div>
                      <time dateTime={reservation.date} className="mt-1 block font-semibold">{reservation.dateLabel}</time>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500">Time &amp; party</div>
                      <time dateTime={reservation.dateTime} className="mt-1 block font-semibold">{reservation.time} · {reservation.guestCount} people</time>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleReservationInterest(reservation)}
                    aria-label={`Request ${reservation.restaurant} on ${reservation.dateLabel} at ${reservation.time} for ${reservation.guestCount} people`}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#8f3d24,#b45a33)] px-6 py-4 text-base font-bold text-[#fff8f1] shadow-[0_16px_40px_rgba(143,61,36,0.2)] transition hover:-translate-y-0.5"
                  >
                    I&apos;m interested
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="mx-auto max-w-6xl px-5 py-20 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">Features</div>
            <h2 className="mt-3 font-['Fraunces'] text-4xl leading-tight text-stone-900">
              A concierge flow designed around real reservation release behavior.
            </h2>
            <p className="mt-4 text-lg leading-8 text-stone-600">
              The service is centered around clear customer intake, continuous availability monitoring, fast success alerts, and a clean Tock account handoff for Hayato, Atomix, Fu Hui Hua, The French Laundry, and other competitive restaurants.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {featureCards.map((card, index) => (
              <article
                key={card.title}
                className={`rounded-[1.8rem] border p-7 shadow-sm ${
                  index === 2
                    ? "border-emerald-200 bg-[linear-gradient(180deg,#f3fbf8,#e6f4ef)]"
                    : "border-stone-200 bg-white"
                }`}
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-stone-900 text-sm font-bold text-[#fff8f1]">
                  0{index + 1}
                </div>
                <h3 className="font-['Fraunces'] text-2xl text-stone-900">{card.title}</h3>
                <p className="mt-4 text-base leading-7 text-stone-600">{card.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="how-it-works" className="bg-[#1d403c] py-20 text-[#f8f1e8]">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-100/70">How it works</div>
              <h2 className="mt-3 font-['Fraunces'] text-4xl leading-tight">
                From request to credentials, every step is explicit.
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
              {steps.map((step, index) => (
                <div
                  key={step.title}
                  className="flow-card rounded-[1.8rem] border border-white/12 bg-white/6 p-7 backdrop-blur-sm"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-100/70">Step {index + 1}</div>
                  <h3 className="mt-3 font-['Fraunces'] text-2xl">{step.title}</h3>
                  <p className="mt-4 text-base leading-7 text-emerald-50/78">{step.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="reviews" aria-labelledby="customer-reviews-heading" className="border-y border-stone-200/70 bg-[#fff8f1] py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">Customer reviews</div>
              <h2 id="customer-reviews-heading" className="mt-3 font-['Fraunces'] text-4xl leading-tight text-stone-900">
                Real feedback from completed booking handoffs.
              </h2>
              <p className="mt-4 text-lg leading-8 text-stone-600">
                Customers share the details that matter most: an easy process, a confirmed account handoff, and clear follow-up. Names, email addresses, handles, and phone numbers have been redacted for privacy.
              </p>
            </div>

            <div className="grid items-start gap-6 lg:grid-cols-3">
              {reviewImages.map((review) => (
                <figure key={review.src} className="self-start overflow-hidden rounded-[1.8rem] border border-stone-200 bg-white shadow-[0_14px_40px_rgba(63,41,27,0.08)]">
                  <div className="border-b border-stone-200 bg-stone-950 p-2">
                    <img src={review.src} alt={review.alt} loading="lazy" className="block h-auto w-full rounded-[1.35rem]" />
                  </div>
                  <figcaption className="px-6 py-5 text-sm font-bold uppercase tracking-[0.16em] text-stone-600">
                    {review.caption} · identity redacted
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
            <div>
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">Best fit</div>
              <h2 className="mt-3 font-['Fraunces'] text-4xl leading-tight text-stone-900">
                Best for diners who want the table, not the refresh routine.
              </h2>
              <p className="mt-4 text-lg leading-8 text-stone-600">
                If you are trying to book limited restaurant drops on Tock, especially Hayato reservations, Atomix reservations, Fu Hui Hua reservations, or The French Laundry reservations, Tock Buddy turns the chase into a concierge request with human-friendly updates and clear payment timing.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-[1.6rem] border border-stone-200 bg-white p-6 shadow-sm">
                <h3 className="font-['Fraunces'] text-2xl text-stone-900">Restaurants with fixed release times</h3>
                <p className="mt-3 text-base leading-7 text-stone-600">Useful when a Tock restaurant opens seats every Friday or on the first day of the month and you want someone watching ahead of time.</p>
              </div>
              <div className="rounded-[1.6rem] border border-stone-200 bg-white p-6 shadow-sm">
                <h3 className="font-['Fraunces'] text-2xl text-stone-900">Any-date booking behavior</h3>
                <p className="mt-3 text-base leading-7 text-stone-600">Useful when you do not care about one exact reservation date and simply want the first available bookable slot.</p>
              </div>
              <div className="rounded-[1.6rem] border border-stone-200 bg-white p-6 shadow-sm">
                <h3 className="font-['Fraunces'] text-2xl text-stone-900">Account created in your name</h3>
                <p className="mt-3 text-base leading-7 text-stone-600">The reservation is secured through a Tock account created for the customer, then released after the concierge deposit is received.</p>
              </div>
              <div className="rounded-[1.6rem] border border-stone-200 bg-white p-6 shadow-sm">
                <h3 className="font-['Fraunces'] text-2xl text-stone-900">Fast success coordination</h3>
                <p className="mt-3 text-base leading-7 text-stone-600">When a booking is found, email and text alerts make the deposit and credential handoff easy to complete quickly.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-stone-200/70 bg-[#fff8f1] py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">Supported restaurants</div>
              <h2 className="mt-3 font-['Fraunces'] text-4xl leading-tight text-stone-900">
                Tock Buddy is tuned for the hardest tables to catch.
              </h2>
              <p className="mt-4 text-lg leading-8 text-stone-600">
                Start with a focused set of competitive Tock restaurants, or request another restaurant and we will review whether the concierge watch is a fit.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {supportedRestaurants.map((restaurant) => (
                <article key={restaurant.name} className="rounded-[1.8rem] border border-stone-200 bg-white p-7 shadow-sm">
                  <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">{restaurant.location}</div>
                  <h3 className="mt-3 font-['Fraunces'] text-3xl text-stone-900">{restaurant.name}</h3>
                  <p className="mt-4 text-base leading-7 text-stone-600">{restaurant.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="booking-request" ref={bookingRequestRef} className="border-y border-stone-200/70 bg-[#fffaf2] py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
              <div>
                <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">Booking concierge</div>
                <h2 className="mt-3 font-['Fraunces'] text-4xl leading-tight text-stone-900">
                  Submit a manual booking request.
                </h2>
                <p className="mt-4 text-lg leading-8 text-stone-600">
                  Tell us where you want to dine, how flexible your date is, and how to reach you. Tock Buddy will create a Tock account in the customer&apos;s name and monitor for availability around the clock.
                </p>

                <div className="mt-8 rounded-[1.8rem] border border-stone-200 bg-white p-6 shadow-sm">
                  <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">Concierge flow</div>
                  <div className="mt-4 space-y-3 text-base leading-7 text-stone-600">
                    <p>1. Customer requests a reservation through this form.</p>
                    <p>2. A Tock account is created in the customer&apos;s name to guarantee entrance to the restaurant.</p>
                    <p>3. Tock Buddy runs 24/7 to find an available booking.</p>
                    <p>4. Once a booking is found, the customer gets email and text alerts to pay the $100/person concierge fee.</p>
                    <p>5. Upon receipt of deposit, the Tock account is released to the customer with login credentials.</p>
                  </div>
                </div>

                <div className="mt-4 rounded-[1.8rem] border border-emerald-200 bg-[linear-gradient(180deg,#f2fbf7,#e6f5ee)] p-6 shadow-sm">
                  <div className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-800">Important note</div>
                  <p className="mt-3 text-base leading-7 text-emerald-950/80">
                    This is request intake only. The concierge fee is $100/person if successful. We do not collect card details on this website. Payment is collected after a booking is found and before the Tock account handoff.
                  </p>
                </div>
              </div>

              <div className="rounded-[2rem] border border-stone-200 bg-white p-7 shadow-[0_18px_50px_rgba(63,41,27,0.1)]">
                <form className="space-y-5" onSubmit={handleBookingSubmit}>
                  <div className="grid gap-5 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-sm font-bold text-stone-700">First name</span>
                      <input
                        className="w-full rounded-2xl border border-stone-300 bg-[#fffdf9] px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="First name"
                        autoComplete="given-name"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-bold text-stone-700">Last name</span>
                      <input
                        className="w-full rounded-2xl border border-stone-300 bg-[#fffdf9] px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Last name"
                        autoComplete="family-name"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-bold text-stone-700">Email</span>
                      <input
                        className="w-full rounded-2xl border border-stone-300 bg-[#fffdf9] px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                        autoComplete="email"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="mb-2 block text-sm font-bold text-stone-700">Phone number</span>
                    <input
                      className="w-full rounded-2xl border border-stone-300 bg-[#fffdf9] px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 555-5555"
                      autoComplete="tel"
                    />
                    <p className="mt-2 text-sm leading-6 text-stone-500">Used for immediate communication if we secure the reservation and need to coordinate fast handoff.</p>
                  </label>

                  <div className="grid gap-5 md:grid-cols-[1fr_auto]">
                    <label className="block">
                      <span className="mb-2 block text-sm font-bold text-stone-700">Restaurant</span>
                      <select
                        className="w-full rounded-2xl border border-stone-300 bg-[#fffdf9] px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500"
                        name="restaurant"
                        value={formData.restaurant}
                        onChange={handleInputChange}
                      >
                        {bookingRestaurantOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>

                    <div className="block">
                      <span className="mb-2 block text-sm font-bold text-stone-700">Guests</span>
                      <div className="flex gap-3">
                        <label className="flex items-center gap-2 rounded-2xl border border-stone-300 px-4 py-3 text-sm font-semibold text-stone-700">
                          <input
                            type="radio"
                            name="guestCount"
                            value="1"
                            checked={formData.guestCount === "1"}
                            onChange={handleInputChange}
                          />
                          1
                        </label>
                        <label className="flex items-center gap-2 rounded-2xl border border-stone-300 px-4 py-3 text-sm font-semibold text-stone-700">
                          <input
                            type="radio"
                            name="guestCount"
                            value="2"
                            checked={formData.guestCount === "2"}
                            onChange={handleInputChange}
                          />
                          2
                        </label>
                      </div>
                    </div>
                  </div>

                  {formData.restaurant === "Other Tock restaurant" && (
                    <label className="block">
                      <span className="mb-2 block text-sm font-bold text-stone-700">Restaurant name</span>
                      <input
                        className="w-full rounded-2xl border border-stone-300 bg-[#fffdf9] px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500"
                        name="customRestaurant"
                        value={formData.customRestaurant}
                        onChange={handleInputChange}
                        placeholder="Enter the restaurant name"
                      />
                    </label>
                  )}

                  <label className="block">
                    <span className="mb-2 block text-sm font-bold text-stone-700">Notes</span>
                    <textarea
                      className="min-h-[120px] w-full rounded-2xl border border-stone-300 bg-[#fffdf9] px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Enter any special occasions or food allergies"
                    />
                  </label>

                  <div className="rounded-[1.6rem] border border-stone-200 bg-[#fcf7f1] p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-stone-800">Date preference</div>
                        <div className="mt-1 text-sm text-stone-600">Choose a specific date or allow any available date.</div>
                      </div>
                      <label className="flex shrink-0 items-center gap-2 text-sm font-semibold text-stone-700">
                        <input
                          type="checkbox"
                          name="anyDate"
                          checked={formData.anyDate}
                          onChange={handleInputChange}
                        />
                        Any date
                      </label>
                    </div>

                    <div className="mt-4 grid min-w-0 grid-cols-[minmax(0,1fr)]">
                      <input
                        className="booking-date-input block w-full min-w-0 max-w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-base text-stone-900 outline-none transition focus:border-stone-500 disabled:cursor-not-allowed disabled:bg-stone-100"
                        name="desiredDate"
                        type="date"
                        value={formData.desiredDate}
                        onChange={handleInputChange}
                        disabled={formData.anyDate}
                      />
                    </div>
                  </div>

                  <div className="rounded-[1.6rem] border border-stone-200 bg-[#faf4ea] px-4 py-4 text-sm leading-7 text-stone-700">
                    If the reservation is secured, you will receive email and text alerts. The one-time concierge fee is $100/person if successful, collected before we release the Tock account credentials created for your reservation.
                  </div>

                  <button
                    type="submit"
                    disabled={submitState.status === "submitting"}
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#8f3d24,#b45a33)] px-6 py-4 text-base font-bold text-[#fff8f1] shadow-[0_16px_40px_rgba(143,61,36,0.2)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {submitState.status === "submitting" ? "Submitting request..." : "Submit booking request"}
                  </button>

                  {submitState.message && (
                    <div
                      className={`rounded-2xl px-4 py-4 text-sm leading-7 ${
                        submitState.status === "success"
                          ? "border border-emerald-200 bg-emerald-50 text-emerald-900"
                          : submitState.status === "error"
                            ? "border border-red-200 bg-red-50 text-red-900"
                            : "border border-stone-200 bg-stone-50 text-stone-700"
                      }`}
                    >
                      {submitState.message}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="border-y border-stone-200/70 bg-[#fffaf2] py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-stone-500">FAQ</div>
              <h2 className="mt-3 font-['Fraunces'] text-4xl leading-tight text-stone-900">
                Questions before submitting a concierge request.
              </h2>
            </div>

            <div className="grid gap-4">
              {faqs.map((faq) => (
                <article key={faq.question} className="rounded-[1.6rem] border border-stone-200 bg-white p-6 shadow-sm">
                  <h3 className="font-['Fraunces'] text-2xl text-stone-900">{faq.question}</h3>
                  <p className="mt-3 text-base leading-7 text-stone-600">{faq.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] border border-stone-200 bg-[linear-gradient(135deg,#8f3d24,#57311e)] p-8 text-[#fff8f1] shadow-[0_20px_60px_rgba(77,39,22,0.25)] md:p-12">
            <div className="max-w-3xl">
              <div className="text-sm font-bold uppercase tracking-[0.22em] text-orange-100/75">Ready</div>
              <h2 className="mt-3 font-['Fraunces'] text-4xl leading-tight">
                Start with the manual booking form.
              </h2>
              <p className="mt-4 text-lg leading-8 text-orange-50/88">
                Send the request once, then let Tock Buddy keep watching. You only pay the $100/person concierge fee after a booking is found.
              </p>
              <div className="mt-8">
                <a
                  href="#booking-request"
                  className="inline-flex items-center justify-center rounded-2xl bg-[#fff8f1] px-6 py-4 text-base font-bold text-[#7d351f] transition hover:-translate-y-0.5"
                >
                  Submit booking request
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-stone-200 bg-[#f7f1e8]">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-8 text-sm text-stone-600 md:flex-row md:items-center md:justify-between lg:px-8">
          <div>Tock Buddy concierge booking requests for hard-to-get Tock reservations.</div>
          <div>Built for reservation monitoring, success alerts, and clean account handoff.</div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
